import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import InstitutionNavbar from "../components/instituteNavbar";
import { FaUserCircle, FaMapMarkerAlt, FaBriefcase, FaEnvelope, FaPhone, FaArrowLeft, FaFileDownload, FaGraduationCap } from "react-icons/fa";
import { Clock, Bookmark, Mail, IndianRupee } from "lucide-react";
import "../styles/GlobalStyles.css"; // Reuse global styles

const CandidateDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [candidate, setCandidate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaved, setIsSaved] = useState(false);
    const [isInvited, setIsInvited] = useState(false);

    useEffect(() => {
        const fetchCandidate = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/institutionfilter/candidate/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setCandidate(res.data);
                if (res.data) {
                    setIsSaved(res.data.is_saved);
                    setIsInvited(res.data.is_invited);
                }
            } catch (err) {
                console.log(err);
                setError("Failed to load candidate details or profile is locked.");
            } finally {
                setLoading(false);
            }
        };
        fetchCandidate();
    }, [id]);

    const handleSave = async () => {
        if (!candidate) return;
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/institutionfilter/save-candidate`,
                { candidateId: candidate._id || candidate.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setIsSaved(response.data.isSaved);
        } catch (err) {
            console.error(err);
            alert("Error saving candidate");
        }
    };

    const handleInvite = async () => {
        if (!candidate) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/institutionfilter/invite-candidate`,
                { candidateId: candidate._id || candidate.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setIsInvited(true);
            alert("Invitation sent!");
        } catch (err) {
            console.error(err);
            alert("Error inviting candidate");
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Profile...</div>;
    if (error) return (
        <div className="p-8 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button onClick={() => navigate(-1)} className="secondary-btn">Go Back</button>
        </div>
    );

    if (!candidate) return <div>No data found.</div>;

    const isLocked = candidate.email === "Locked";

    return (
        <div className="page-wrapper">
            <InstitutionNavbar />

            <div
                className="page-container container-wide mt-[120px]"
            >

                <div className="profile-dashboard">
                    <button onClick={() => navigate(-1)} className="secondary-btn mb-4 flex items-center gap-2">
                        <FaArrowLeft /> Back to Results
                    </button>

                    <div className="profile-card w-full">
                        {/* Header / Profile Summary */}
                        <div className="flex flex-col md:flex-row gap-8 items-start md:items-center mb-8 pb-8 border-b border-gray-200">
                            <div className="shrink-0">
                                {candidate.profilePic ? (
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}/${candidate.profilePic.replace(/\\/g, "/")}`}
                                        className="profile-avatar w-32 h-32 object-cover"
                                        alt="Profile"
                                    />
                                ) : (
                                    <FaUserCircle size={128} className="text-gray-300 profile-avatar" />
                                )}
                            </div>

                            <div className="flex-1">
                                <h1 className="page-title mb-4">
                                    {candidate.first_name} {candidate.last_name}
                                </h1>

                                <div className="flex flex-wrap gap-4 text-gray-600 text-sm">
                                    <span className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"><FaMapMarkerAlt className="text-blue-500" /> {candidate.address?.area && `${candidate.address.area}, `}{candidate.address?.city}, {candidate.address?.state}</span>
                                    <span className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"><FaBriefcase className="text-blue-500" /> {candidate.experience || candidate.total_experience || 0} Years Exp.</span>
                                    {candidate.salary && (
                                        <span className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"><IndianRupee size={14} className="text-blue-500" /> {Number(candidate.salary).toLocaleString('en-IN')}</span>
                                    )}
                                    {candidate.notice_period && (
                                        <span className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"><Clock size={14} className="text-blue-500" /> {candidate.notice_period} Days Notice</span>
                                    )}

                                </div>
                                <div className="mt-3">
                                    <span className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full w-fit"><FaGraduationCap size={15} className="text-blue-500" /> {candidate.qualification?.qualification || "Not Specified"}</span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 min-w-[160px]">
                                {isLocked ? (
                                    <div className="bg-amber-50 text-amber-700 border border-amber-200 px-4 py-2 rounded-lg text-sm font-bold text-center">
                                        LOCKED PROFILE
                                    </div>
                                ) : (
                                    <div className="bg-green-50 text-green-700 border border-green-200 px-4 py-2 rounded-lg text-sm font-bold text-center">
                                        UNLOCKED
                                    </div>
                                )}

                                {/* Actions */}
                                <button
                                    onClick={handleInvite}
                                    disabled={isInvited}
                                    className={`primary-btn w-full flex items-center justify-center btn-invite ${isInvited ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : ''}`}
                                >
                                    <Mail size={16} className="mr-2" /> {isInvited ? "Invited" : "Invite"}
                                </button>

                                <button
                                    onClick={handleSave}
                                    className={`secondary-btn w-full flex items-center justify-center ${isSaved ? 'bg-blue-50 text-blue-600 border-blue-200' : ''}`}
                                >
                                    <Bookmark size={16} fill={isSaved ? "currentColor" : "none"} className="mr-2" /> {isSaved ? "Saved" : "Save"}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                            {/* Left Col: Contact & Skills */}
                            <div className="space-y-8">
                                <section className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <h3 className="section-header mt-0 border-b border-gray-200 pb-2 mb-4">Contact Info</h3>
                                    <div className="space-y-4 text-sm font-medium text-gray-700">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-blue-500 shadow-sm">
                                                <FaEnvelope />
                                            </div>
                                            <span>{candidate.email}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-blue-500 shadow-sm">
                                                <FaPhone />
                                            </div>
                                            <span>{candidate.phone_number}</span>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h3 className="section-header">Skills</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {candidate.skills?.map((s, i) => (
                                            <span key={i} className="skill-pill-interactive bg-white cursor-default">
                                                {typeof s === 'string' ? s : s.skill}
                                            </span>
                                        )) || <span className="text-gray-400 text-sm">No skills listed</span>}
                                    </div>
                                </section>

                                {/* Professional Summary */}
                                {candidate.professional_Description && (
                                    <section>
                                        <h3 className="section-header">About</h3>
                                        <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-100 text-justify">
                                            {candidate.professional_Description}
                                        </p>
                                    </section>
                                )}
                            </div>

                            {/* Right Col: Detailed Info */}
                            <div className="md:col-span-2 space-y-8">



                                {/* Work Experience */}
                                <section>
                                    <h3 className="section-header">Experience</h3>
                                    <div className="space-y-6">
                                        {candidate.work_experience?.map((exp, i) => (
                                            <div key={i} className="relative pl-6 border-l-2 border-blue-100 pb-4 last:pb-0">
                                                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-4 border-blue-500 shadow-sm"></div>
                                                <h4 className="font-bold text-gray-900 text-lg">{exp.position}</h4>
                                                <p className="text-blue-600 font-medium mb-1">{exp.organisation}</p>
                                                <p className="text-xs text-gray-500 mb-3 bg-gray-100 inline-block px-2 py-1 rounded">
                                                    {new Date(exp.start_date || Date.now()).getFullYear()} - {exp.working_till_date ? "Present" : new Date(exp.end_date || Date.now()).getFullYear()}
                                                </p>
                                                <p className="text-sm text-gray-600 leading-relaxed">{exp.responsibilities || ""}</p>
                                            </div>
                                        ))}
                                        {(!candidate.work_experience || candidate.work_experience.length === 0) && <p className="text-gray-500 italic">No experience added.</p>}
                                    </div>
                                </section>

                                {/* Education */}
                                <section>
                                    <h3 className="section-header">Education</h3>
                                    <div className="grid gap-4">
                                        {candidate.education?.map((edu, i) => (
                                            <div key={i} className="education-card p-4 flex justify-between items-start border-l-4 border-l-blue-500">
                                                <div>
                                                    <h4 className="font-bold text-gray-900">{edu.education === '10th' ? 'Class 10th' : edu.education === '12th' ? 'Class 12th' : edu.degree || edu.education}</h4>
                                                    <p className="text-gray-600">{edu.institution_name}</p>
                                                </div>
                                                <span className="text-sm text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                                                    {edu.year_of_passing || new Date(edu.end_date || Date.now()).getFullYear()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {/* Resume Download */}
                                {candidate.resume && candidate.resume !== "Locked" && (
                                    <section className="mt-8 pt-6 border-t border-gray-100">
                                        <h3 className="section-header mb-4">Resume</h3>
                                        <a
                                            href={`${process.env.REACT_APP_API_URL}/${candidate.resume.replace(/\\/g, "/")}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="primary-btn inline-flex items-center gap-2 no-underline hover:text-white btn-resume"
                                        >
                                            <FaFileDownload /> Download Resume
                                        </a>
                                    </section>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CandidateDetails;
